import { FlexColumn, FlexRow, FullContainer, Loading } from '@gorila-shared-ui/components';
import { StyledDivider } from 'baseui/divider';
import { TriangleDown, TriangleUp } from 'baseui/icon';
import { Pagination } from 'baseui/pagination';
import {
  SortableHeadCell,
  StyledBody,
  StyledCell,
  StyledHead,
  StyledHeadCell,
  StyledRow,
  StyledTable,
} from 'baseui/table';
import { LabelMedium, ParagraphSmall } from 'baseui/typography';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import { useStyles } from '../../hooks/useStyles';
import { PaginatedColumn } from '../../types/app';

type Props = {
  data?: {
    items: any[];
    id?: string;
    onClick?: () => void;
  }[];
  columns: PaginatedColumn[];
  onSort?: (column: string) => void;
  page: number;
  totalItems: number;
  onPageChange: (nextPage: number) => void;
  loading?: boolean;
  activeId?: string;
};
export function StyledPaginatedTable({
  data,
  columns,
  page,
  onPageChange,
  totalItems,
  loading = false,
  activeId,
  onSort,
}: Readonly<Props>) {
  const { css, theme, classes } = useStyles();
  const itemsPerPage = 50;
  const { isMedium } = useBreakpoints();

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1) {
      return;
    }
    if (nextPage > Math.ceil(totalItems / itemsPerPage)) {
      return;
    }
    onPageChange(nextPage);
  };

  const tableWidthFromColumns = columns.reduce((accummulated, column) => accummulated + (column.minWidth ?? 0), 0);
  const tableWidth = tableWidthFromColumns > 1000 ? tableWidthFromColumns : 1000;

  return (
    <FullContainer
      verticalFlex
      classNames={`${css({ gap: theme.sizing.scale300 })}`}
    >
      <StyledTable>
        <StyledHead
          $width={`${tableWidth}px`}
          style={{ minWidth: '100%' }}
        >
          {columns.map((column) => (
            <>
              {column.sorteable && onSort && (
                <SortableHeadCell
                  key={column.key}
                  title={column.label}
                  direction={column.sortDirection}
                  onSort={() => onSort(column.label)}
                  fillClickTarget
                  overrides={{
                    SortableLabel: {
                      component: ({ children }) => {
                        return (
                          <FlexRow
                            gap={'8px'}
                            classNames={css({ flex: 1 })}
                          >
                            <ParagraphSmall
                              className={css({ flex: 1 })}
                              margin={0}
                            >
                              {children[1]}
                            </ParagraphSmall>
                            <FlexColumn gap={0}>
                              <TriangleUp
                                size={'12px'}
                                color={
                                  children[0].props.direction === 'ASC' ? undefined : theme.colors.buttonDisabledText
                                }
                              />
                              <TriangleDown
                                size={'12px'}
                                color={
                                  children[0].props.direction === 'DESC' ? undefined : theme.colors.buttonDisabledText
                                }
                              />
                            </FlexColumn>
                          </FlexRow>
                        );
                      },
                    },
                    HeadCell: {
                      style: () => ({
                        minWidth: column.minWidth ? `${column.minWidth}px` : undefined,
                        maxWidth: column.maxWidth ? `${column.maxWidth}px` : undefined,
                        flex: column.flex ? column.flex : 1,
                        boxSizing: 'border-box',
                        justifyContent: column.centered ? 'center' : undefined,
                        textAlign: column.centered ? 'center' : undefined,
                        alignItems: 'center',
                      }),
                    },
                  }}
                />
              )}
              {!column.sorteable && (
                <StyledHeadCell
                  key={column.key}
                  style={{
                    minWidth: column.minWidth ? `${column.minWidth}px` : undefined,
                    maxWidth: column.maxWidth ? `${column.maxWidth}px` : undefined,
                    flex: column.flex ? column.flex : 1,
                    boxSizing: 'border-box',
                    justifyContent: column.centered ? 'center' : undefined,
                    textAlign: column.centered ? 'center' : undefined,
                    alignItems: 'center',
                  }}
                >
                  {column.label}
                </StyledHeadCell>
              )}
            </>
          ))}
        </StyledHead>
        <StyledBody
          $width={`${tableWidth}px`}
          style={{ minWidth: '100%' }}
        >
          {loading && <Loading />}
          {!loading &&
            data?.map((row, rowIndex) => (
              <>
                {rowIndex !== 0 && <StyledDivider style={{ width: '100%', marginTop: 0, marginBottom: 0 }} />}
                <StyledRow
                  key={rowIndex}
                  onClick={() => row.onClick && row.onClick()}
                  className={`
                    ${row.onClick ? css({ cursor: 'pointer' }) : ''} 
                    ${activeId && row.id === activeId ? css({ backgroundColor: theme.colors.primary100 }) : ''}
                    ${css({ backgroundColor: (rowIndex + 1) % 2 === 0 ? theme.colors.backgroundSecondary : '' })}
                  `}
                >
                  {row.items.map((item, itemIndex) => (
                    <StyledCell
                      key={`${rowIndex}-${itemIndex}`}
                      style={{
                        minWidth: columns[itemIndex].minWidth ? `${columns[itemIndex].minWidth}px` : undefined,
                        maxWidth: columns[itemIndex].maxWidth ? `${columns[itemIndex].maxWidth}px` : undefined,
                        flex: columns[itemIndex].flex ? columns[itemIndex].flex : undefined,
                        boxSizing: 'border-box',
                        justifyContent: columns[itemIndex].centered ? 'center' : undefined,
                      }}
                    >
                      {item}
                    </StyledCell>
                  ))}
                </StyledRow>
              </>
            ))}
        </StyledBody>
      </StyledTable>
      <FlexRow
        classNames={`${classes.centeredEnd} ${css({
          justifyContent: !isMedium ? 'space-between' : 'end',
        })}`}
      >
        {!isMedium && <LabelMedium>Total: {totalItems}</LabelMedium>}
        <Pagination
          labels={{
            nextButton: !isMedium ? 'Siguiente' : 'Sig.',
            preposition: 'de',
            prevButton: !isMedium ? 'Anterior' : 'Ant.',
          }}
          size="compact"
          currentPage={page}
          numPages={Math.ceil(totalItems / itemsPerPage)}
          onPageChange={({ nextPage }) => handlePageChange(nextPage)}
        />
      </FlexRow>
    </FullContainer>
  );
}
